export const MARKETING_LANGS = ["fr", "en", "es"] as const;
export type MarketingLang = (typeof MARKETING_LANGS)[number];
export const DEFAULT_LANG: MarketingLang = "fr";

export const PAGE_IDS = [
  "home",
  "ownerAccount",
  "managerAccount",
  "management",
  "network",
  "sharing",
  "requests",
  "messaging",
  "ownerPortal",
  "partnerAccount",
  "pricing",
  "about",
  "contact",
  "help",
  "privacy",
  "terms",
] as const;

export type MarketingPageId = (typeof PAGE_IDS)[number];

export const PAGE_PATHS: Record<MarketingPageId, Record<MarketingLang, string>> = {
  home: { fr: "/", en: "/en", es: "/es" },
  ownerAccount: { fr: "/proprietaire", en: "/en/owner-account", es: "/es/cuenta-propietario" },
  managerAccount: { fr: "/gestionnaire", en: "/en/manager-account", es: "/es/cuenta-gestor" },
  management: { fr: "/gestion-locative", en: "/en/property-management", es: "/es/gestion-alquiler" },
  network: { fr: "/reseau-partenaires", en: "/en/partner-network", es: "/es/red-socios" },
  sharing: { fr: "/partage-inventaire", en: "/en/inventory-sharing", es: "/es/compartir-inventario" },
  requests: { fr: "/demandes-reservations", en: "/en/booking-requests", es: "/es/solicitudes-reserva" },
  messaging: { fr: "/messagerie-partenaires", en: "/en/partner-messaging", es: "/es/mensajeria-socios" },
  ownerPortal: { fr: "/portail-proprietaires", en: "/en/owner-portal", es: "/es/portal-propietarios" },
  partnerAccount: { fr: "/compte-partenaire", en: "/en/partner-account", es: "/es/cuenta-socio" },
  pricing: { fr: "/tarifs", en: "/en/pricing", es: "/es/precios" },
  about: { fr: "/about", en: "/en/about", es: "/es/about" },
  contact: { fr: "/contact", en: "/en/contact", es: "/es/contact" },
  help: { fr: "/help", en: "/en/help", es: "/es/help" },
  privacy: { fr: "/privacy", en: "/en/privacy", es: "/es/privacy" },
  terms: { fr: "/terms", en: "/en/terms", es: "/es/terms" },
};

export const NETWORK_NAV_IDS: MarketingPageId[] = [
  "network",
  "sharing",
  "requests",
  "messaging",
];

export const PRODUCT_NAV_IDS: MarketingPageId[] = [
  "management",
  ...NETWORK_NAV_IDS,
  "ownerPortal",
];

export const AUDIENCE_NAV_IDS: MarketingPageId[] = [
  "ownerAccount",
  "managerAccount",
  "partnerAccount",
];

export const HEADER_NAV_IDS: MarketingPageId[] = [...AUDIENCE_NAV_IDS, "pricing", "help"];

export const HASH_REDIRECTS: Record<string, MarketingPageId> = {
  manage: "management",
  network: "network",
  pricing: "pricing",
  how: "home",
  founder: "pricing",
};

export const AUTH_PREFIXES = [
  "/login",
  "/signup",
  "/forgot-password",
  "/update-password",
  "/verify-email",
  "/access",
] as const;

export const APP_PREFIXES = [
  "/dashboard",
  "/progress",
  "/onboarding",
  "/my-organization",
  "/settings",
  "/support",
  "/notifications",
  "/subscription",
  "/plus",
  "/finance-dashboard",
  "/owner-settlements",
  "/cash-accounts",
  "/network",
  "/properties",
  "/owners",
  "/bookings",
  "/payments",
  "/owner-payouts",
  "/expenses",
  "/issues",
  "/ledger-entries",
  "/locataires",
  "/tenant-leases",
  "/deposit-entries",
  "/rent-entries",
  "/expense-categories",
  "/owner-statements",
  "/calendar",
  "/admin",
  "/owner",
  "/tenants",
  "/contact-requests",
] as const;

export function normalizePath(pathname: string): string {
  const trimmed = pathname.trim();
  if (!trimmed || trimmed === "/") return "/";
  const withoutQuery = trimmed.split("?")[0].split("#")[0];
  const collapsed = withoutQuery.replace(/\/+$/, "");
  return collapsed || "/";
}

export function isMarketingLang(value: string | null | undefined): value is MarketingLang {
  return MARKETING_LANGS.includes((value || "").slice(0, 2).toLowerCase() as MarketingLang);
}

export function normalizeLang(value: string | null | undefined): MarketingLang {
  const code = (value || "").slice(0, 2).toLowerCase();
  return isMarketingLang(code) ? code : DEFAULT_LANG;
}

export function langFromPath(pathname: string): MarketingLang {
  const path = normalizePath(pathname);
  if (path === "/en" || path.startsWith("/en/")) return "en";
  if (path === "/es" || path.startsWith("/es/")) return "es";
  return DEFAULT_LANG;
}

export function stripLangPrefix(pathname: string): string {
  const path = normalizePath(pathname);
  if (path === "/en" || path === "/es") return "/";
  if (path.startsWith("/en/")) return path.slice(3) || "/";
  if (path.startsWith("/es/")) return path.slice(3) || "/";
  return path;
}

export function pagePath(id: MarketingPageId, lang: MarketingLang = DEFAULT_LANG): string {
  return PAGE_PATHS[id][lang];
}

export function pathToPageId(pathname: string): MarketingPageId | null {
  const path = normalizePath(pathname);
  for (const id of PAGE_IDS) {
    for (const lang of MARKETING_LANGS) {
      if (PAGE_PATHS[id][lang] === path) return id;
    }
  }
  return null;
}

export function localizeHelpPath(helpPath: string, lang: MarketingLang): string {
  const stripped = stripLangPrefix(helpPath);
  const base = stripped === "/" ? "/help" : stripped.startsWith("/help") ? stripped : `/help${stripped}`;
  if (lang === DEFAULT_LANG) return base;
  return `/${lang}${base}`;
}

export function translatePath(pathname: string, targetLang: MarketingLang): string {
  const path = normalizePath(pathname);
  const id = pathToPageId(path);
  if (id && id !== "help") return PAGE_PATHS[id][targetLang];

  const withoutLang = stripLangPrefix(path);
  if (withoutLang === "/help" || withoutLang.startsWith("/help/")) {
    return localizeHelpPath(withoutLang, targetLang);
  }
  if (withoutLang === "/politica-de-privacidad" || withoutLang === "/privacy") {
    return PAGE_PATHS.privacy[targetLang];
  }
  if (withoutLang === "/condiciones-de-uso" || withoutLang === "/terminos-de-uso" || withoutLang === "/terms") {
    return PAGE_PATHS.terms[targetLang];
  }
  return PAGE_PATHS.home[targetLang];
}

export function isHelpPath(pathname: string): boolean {
  const stripped = stripLangPrefix(pathname);
  return stripped === "/help" || stripped.startsWith("/help/");
}

export function isMarketingPath(pathname: string): boolean {
  const path = normalizePath(pathname);
  if (pathToPageId(path)) return true;
  if (isHelpPath(path)) return true;
  if (path === "/politica-de-privacidad" || path === "/condiciones-de-uso" || path === "/terminos-de-uso") {
    return true;
  }
  return false;
}

export function isAuthPath(pathname: string): boolean {
  const path = normalizePath(pathname);
  return AUTH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function isAuthenticatedAppPath(pathname: string): boolean {
  const path = normalizePath(pathname);
  return APP_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function isPublicShellPath(pathname: string): boolean {
  return !isAuthenticatedAppPath(pathname);
}

export function signupWithIntent(intent: "manager" | "partner" | "owner"): string {
  return `/signup?intent=${intent}`;
}

export function alternatePaths(pathname: string): Record<MarketingLang, string> {
  const path = normalizePath(pathname);
  const id = pathToPageId(path);
  if (id) return PAGE_PATHS[id];
  if (isHelpPath(path)) {
    const stripped = stripLangPrefix(path);
    return {
      fr: localizeHelpPath(stripped, "fr"),
      en: localizeHelpPath(stripped, "en"),
      es: localizeHelpPath(stripped, "es"),
    };
  }
  return PAGE_PATHS.home;
}

export const OG_IMAGE_PATH = "/assets/ownerflow/images/og-network.png";
